<!--suppress ALL -->
<template>
  <div>
    <!-- Menu lateral de filtros -->
    <div class="customizer" :class="{'open': isFilterPanelOpen}">
      <!-- Toggler -->
      <b-link class="customizer-toggle d-flex align-items-center justify-content-center" @click="isFilterPanelOpen = !isFilterPanelOpen">
        <feather-icon icon="FilterIcon" size="25" />
      </b-link>

      <!-- Header -->
      <div class="customizer-section d-flex justify-content-between align-items-center">
        <div>
          <h4 class="text-uppercase mb-0">{{ $t('Filters') }}</h4>
          <small>{{ $t('Filter fleet dashboard data') }}</small>
        </div>
        <feather-icon icon="XIcon" size="18" class="cursor-pointer" @click="isFilterPanelOpen = !isFilterPanelOpen" />
      </div>

      <vue-perfect-scrollbar :settings="perfectScrollbarSettings" class="ps-customizer-area scroll-area mb-2">
        <div class="customizer-section">
          <!-- Filtro de Período -->
          <h5 class="font-weight-bold mb-1">{{ $t('Period') }}</h5>
          <flat-pickr
            v-model="dateRange"
            :config="flatpickrConfig"
            class="form-control mb-1"
            :placeholder="$t('Select period')"
            @on-change="ensureAllowedDateRange"
          />
          <small class="text-muted d-block mb-3">{{ $t('Maximum interval: 31 days') }}</small>
          
          <!-- Filtro de Cliente -->
          <h5 class="font-weight-bold mb-1">{{ $t('Customer') }}</h5>
          <vue-autosuggest
            v-model="searchCustomer"
            :suggestions="filteredOptions"
            :input-props="inputProps"
            @selected="selectHandler"
            @input="onInputChange"
            class="mb-3"
          >
            <template slot-scope="{suggestion}">
              <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
              <div v-show="suggestion.item.ownerName">
                <span class="text-muted">{{ $t('Pertence a: ') }}</span>
                <span class="font-weight-bold"> {{ suggestion.item.ownerName }} </span>
              </div>
            </template>
          </vue-autosuggest>
          
          <!-- Filtro de Grupo -->
          <h5 class="font-weight-bold mb-1">{{ $t('Group of Asset') }}</h5>
          <vue-autosuggest
            v-model="searchGroup"
            :suggestions="filteredOptionsGroup"
            :input-props="inputPropsGroup"
            @selected="selectHandlerGroup"
            @input="onInputChangeGroup"
            class="mb-3"
          >
            <template slot-scope="{suggestion}">
              <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
            </template>
          </vue-autosuggest>
          
          <!-- Filtro de Fabricante -->
          <h5 class="font-weight-bold mb-1">{{ $t('Manufacturer') }}</h5>
          <v-select
            v-model="assetManufacturerId"
            :options="manufacturerOptions"
            :reduce="val => val.value"
            :clearable="true"
            :placeholder="$t('Select manufacturer')"
            class="mb-3"
          />
          
          <!-- Filtro de Modelo -->
          <h5 class="font-weight-bold mb-1">{{ $t('Model') }}</h5>
          <v-select
            v-model="assetModelId"
            :options="modelOptions"
            :reduce="val => val.value"
            :clearable="true"
            :disabled="!assetManufacturerId"
            :placeholder="$t('Select model')"
            class="mb-3"
          />
          
          <!-- Filtro de Tipo de Ativo -->
          <h5 class="font-weight-bold mb-1">{{ $t('Asset Type') }}</h5>
          <v-select
            v-model="assetTypeId"
            :options="assetTypeOptions"
            :reduce="val => val.value"
            :clearable="true"
            :placeholder="$t('Select type')"
            class="mb-3"
          />
          
          <!-- Botões de Ação -->
          <b-button
            @click="clearAllFilters"
            variant="outline-secondary"
            class="mb-2 w-100"
            :disabled="isLoadingAll"
          >
            <feather-icon icon="XIcon" class="mr-50" size="16" />
            {{ $t('Clear All Filters') }}
          </b-button>
          
          <b-button
            @click="forceUpdateDashboard"
            variant="primary"
            class="mb-2 w-100"
            :disabled="isLoadingAll"
          >
            <feather-icon 
              icon="RefreshCwIcon" 
              class="mr-50" 
              :class="{'loading-icon': isLoadingAll}"
              size="16" 
            />
            {{ isLoadingAll ? $t('Loading...') : $t('Apply Filters') }}
          </b-button>
        </div>
      </vue-perfect-scrollbar>
    </div>

    <!-- Conteúdo do Dashboard - Layout de grid responsivo -->
    <b-overlay :show="isLoadingAll" rounded="sm">
      <!-- Indicador de atualização -->
      <div v-if="isLoadingAll" class="update-indicator">
        <feather-icon
          icon="RefreshCwIcon"
          size="16"
          class="mr-1 loading-icon"
        />
        <small>{{ $t('Updating data...') }}</small>
      </div>

      <!-- Seção de Indicadores de Contagem de Ativos -->
      <b-row class="match-height mb-2">
        <!-- Card Total de Ativos -->
        <b-col lg="4" md="6" cols="12">
          <b-card class="card-statistics-enhanced card-compact">
            <b-overlay :show="isLoading.assetsCount" rounded="sm">
              <div class="statistics-content">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="d-flex align-items-center">
                    <div class="avatar bg-light-primary p-50 mr-1">
                      <div class="avatar-content">
                        <feather-icon icon="LayersIcon" size="22" class="text-primary" />
                      </div>
                    </div>
                    <div>
                      <h6 class="mb-0 font-weight-medium">{{ $t('Total Assets') }}</h6>
                      <small class="text-muted">{{ $t('Fleet summary') }}</small>
                    </div>
                  </div>
                  <div class="text-right">
                    <h2 class="font-weight-bolder mb-0 text-primary">{{ assetsCountData.totalAssets }}</h2>
                  </div>
                </div>
                <div class="stats-grid mt-1 pt-1 border-top">
                  <div class="stat-item">
                    <div class="stat-icon bg-light-primary">
                      <feather-icon icon="BoxIcon" size="14" class="text-primary" />
                    </div>
                    <div class="stat-content">
                      <span class="stat-label">{{ $t('Types') }}</span>
                      <span class="stat-value text-primary">{{ assetsCountData.byType.length }}</span>
                    </div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-icon bg-light-success">
                      <feather-icon icon="PackageIcon" size="14" class="text-success" />
                    </div>
                    <div class="stat-content">
                      <span class="stat-label">{{ $t('Models') }}</span>
                      <span class="stat-value text-success">{{ assetsCountData.byModel.length }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </b-overlay>
          </b-card>
        </b-col>

        <!-- Card Top Tipos -->
        <b-col lg="4" md="6" cols="12">
          <b-card no-body class="card-browser-states">
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title class="mb-0">
                  <feather-icon icon="BoxIcon" size="18" class="mr-50 text-primary" />
                  {{ $t('By Type') }}
                </b-card-title>
                <b-badge v-if="assetsCountData.byType.length > 0" variant="light-primary" pill>
                  {{ assetsCountData.byType.length }} {{ $t('types') }}
                </b-badge>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.assetsCount" rounded="sm">
                <div v-if="assetsCountData.byType.length === 0" class="text-center text-muted py-2">
                  <small>{{ $t('No data available') }}</small>
                </div>
                <div v-else class="scrollable-list-container">
                  <div class="asset-count-list scrollable-content">
                    <div 
                      v-for="(item, index) in assetsCountData.byType" 
                      :key="`type-${index}`"
                      class="asset-count-item"
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center flex-grow-1">
                          <span class="bullet bullet-sm bullet-primary mr-1"></span>
                          <span class="asset-name">{{ item.name }}</span>
                        </div>
                        <div class="asset-quantity">
                          <b-badge variant="light-primary" pill>{{ item.quantity }}</b-badge>
                        </div>
                      </div>
                      <div v-if="assetsCountData.totalAssets > 0" class="progress-wrapper mt-50">
                        <b-progress 
                          :value="(item.quantity / assetsCountData.totalAssets) * 100" 
                          height="6px"
                          variant="primary"
                          class="progress-bar-primary"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-if="assetsCountData.byType.length > 3" class="scroll-fade-bottom"></div>
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Card Top Modelos -->
        <b-col lg="4" md="12" cols="12">
          <b-card no-body class="card-browser-states">
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title class="mb-0">
                  <feather-icon icon="PackageIcon" size="18" class="mr-50 text-success" />
                  {{ $t('By Model') }}
                </b-card-title>
                <b-badge v-if="assetsCountData.byModel.length > 0" variant="light-success" pill>
                  {{ assetsCountData.byModel.length }} {{ $t('models') }}
                </b-badge>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.assetsCount" rounded="sm">
                <div v-if="assetsCountData.byModel.length === 0" class="text-center text-muted py-2">
                  <small>{{ $t('No data available') }}</small>
                </div>
                <div v-else class="scrollable-list-container">
                  <div class="asset-count-list scrollable-content">
                    <div 
                      v-for="(item, index) in assetsCountData.byModel" 
                      :key="`model-${index}`"
                      class="asset-count-item"
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center flex-grow-1">
                          <span class="bullet bullet-sm bullet-success mr-1"></span>
                          <span class="asset-name">{{ item.name }}</span>
                        </div>
                        <div class="asset-quantity">
                          <b-badge variant="light-success" pill>{{ item.quantity }}</b-badge>
                        </div>
                      </div>
                      <div v-if="assetsCountData.totalAssets > 0" class="progress-wrapper mt-50">
                        <b-progress 
                          :value="(item.quantity / assetsCountData.totalAssets) * 100" 
                          height="6px"
                          variant="success"
                          class="progress-bar-success"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-if="assetsCountData.byModel.length > 3" class="scroll-fade-bottom"></div>
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Banner informativo compacto quando dados não foram carregados -->
      <b-row v-if="!hasLoadedData && !isLoadingAll" class="mb-2">
        <b-col cols="12">
          <div class="filter-alert">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon icon="InfoIcon" size="18" class="text-primary mr-75 pulse-icon" />
                <span class="font-weight-medium">
                  {{ $t('Configure filters to view charts') }}
                </span>
              </div>
              <b-button 
                variant="primary" 
                size="sm"
                @click="isFilterPanelOpen = true"
                class="btn-filter-action"
              >
                <feather-icon icon="FilterIcon" size="14" class="mr-50" />
                {{ $t('Filters') }}
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Layout com Grid para organizar os gráficos -->
      <b-row class="match-height">
        <!-- Card com Status da Frota + Ativos com Falhas -->
        <b-col lg="6" md="12">
          <b-card no-body>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-start w-100">
                <div>
                  <b-card-title class="mb-50">{{ $t('Fleet Status') }}</b-card-title>
                  <small class="text-muted d-block">
                    <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                    {{ currentDate }}
                  </small>
                </div>
                <div class="d-flex align-items-center flex-wrap justify-content-end">
                  <div class="mr-2 mb-50 alert-total d-flex align-items-center">
                    <feather-icon
                      icon="LayersIcon"
                      size="20"
                      class="text-info mr-1"
                    />
                    <span class="font-weight-bold">{{ chartData.totalAssets }}</span>
                    <small class="text-muted ml-1">{{ $t('total assets') }}</small>
                  </div>
                  <div class="mb-50 alert-falhas d-flex align-items-center" >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="20"
                      class="text-warning mr-1"
                    />
                    <span class="font-weight-bold">{{ chartData.assetsWithFailures }}</span>
                    <small class="text-muted ml-1">{{ $t('assets with failures') }}</small>
                  </div>
                </div>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.fleetStatus" rounded="sm" class="d-flex justify-content-center">
                <div v-if="!hasFleetStatusData" class="no-data-container">
                  <feather-icon
                    v-if="hasLoadedData"
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-muted mb-1"
                  />
                  <feather-icon
                    v-else
                    icon="FilterIcon"
                    size="32"
                    class="text-primary mb-1"
                  />
                  <h4 v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data') }}</h4>
                  <h4 v-else class="text-primary">{{ $t('Ready to start!') }}</h4>
                  <p v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data to generate fleet status chart') }}</p>
                  <p v-else class="text-muted">{{ $t('Use the filter menu and click Apply Filters to view dashboard data') }}</p>
                </div>
                <div v-else class="chart-container position-relative">
                  <!-- Indicador de falhas no centro do gráfico -->
                  <div class="failure-indicator" v-if="!isLoading.fleetStatus">
                    <div class="failure-circle" style="margin-top: -5rem;">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="20"
                        class="text-warning mb-1"
                      />
                      <h5 class="m-0 font-weight-bold">{{ chartData.assetsWithFailures }}</h5>
                      <small class="text-muted text-center">{{ $t('assets with failures') }}</small>
                    </div>
                  </div>
                  
                  <chartjs-component-pie-chart
                    v-if="fleetStatusChartData && fleetStatusChartOptions"
                    :data="fleetStatusChartData"
                    :options="fleetStatusChartOptions"
                    height="300"
                    :key="'fleet-status-chart-' + JSON.stringify(chartData.fleetStatus)"
                  />
                  
                  <!-- Informações detalhadas abaixo do gráfico -->
                  <div class="fleet-status-details mt-3">
                    <div class="d-flex justify-content-around stats-container">
                      <div class="status-item">
                        <div class="d-flex align-items-center">
                          <span class="status-bullet working"></span>
                          <span class="status-label">{{ $t('Working') }}</span>
                        </div>
                        <div class="status-value">{{ chartData.fleetStatus && chartData.fleetStatus.highUsage || 0 }}</div>
                      </div>
                      <div class="status-item">
                        <div class="d-flex align-items-center">
                          <span class="status-bullet not-working"></span>
                          <span class="status-label">{{ $t('Not Working') }}</span>
                        </div>
                        <div class="status-value">{{ chartData.fleetStatus && chartData.fleetStatus.lowUsage || 0 }}</div>
                      </div>
                      <div class="status-item">
                        <div class="d-flex align-items-center">
                          <span class="status-bullet inactive"></span>
                          <span class="status-label">{{ $t('No Connection') }}</span>
                        </div>
                        <div class="status-value">{{ chartData.fleetStatus && chartData.fleetStatus.inactive || 0 }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Maior Consumo -->
        <b-col lg="6" md="12">
          <b-card no-body>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title>{{ $t('Highest Consumption') }}</b-card-title>
                <div class="text-muted small">{{ displayDateRange }}</div>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.topConsumptionAssets" rounded="sm" class="d-flex justify-content-center">
                <div v-if="!hasTopConsumptionData" class="no-data-container">
                  <feather-icon
                    v-if="hasLoadedData"
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-muted mb-1"
                  />
                  <feather-icon
                    v-else
                    icon="FilterIcon"
                    size="32"
                    class="text-primary mb-1"
                  />
                  <h4 v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data') }}</h4>
                  <h4 v-else class="text-primary">{{ $t('Waiting for filters') }}</h4>
                  <p v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data to generate consumption chart') }}</p>
                  <p v-else class="text-muted">{{ $t('Apply filters to view this chart') }}</p>
                </div>
                <div v-else class="chart-container" style="height: 300px;">
                  <chartjs-component-bar-chart
                    v-if="topConsumptionChartData && topConsumptionChartOptions"
                    :data="topConsumptionChartData"
                    :options="topConsumptionChartOptions"
                    height="300"
                    :key="'consumption-chart-' + JSON.stringify(chartData.topConsumptionAssets)"
                  />
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Maior Tempo em Uso Severo -->
        <b-col lg="6" md="12">
          <b-card no-body>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title>{{ $t('Highest Severe Use') }}</b-card-title>
                <div class="text-muted small">{{ displayDateRange }}</div>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.topSevereUseAssets" rounded="sm" class="d-flex justify-content-center">
                <div v-if="!hasTopSevereUseData" class="no-data-container">
                  <feather-icon
                    v-if="hasLoadedData"
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-muted mb-1"
                  />
                  <feather-icon
                    v-else
                    icon="FilterIcon"
                    size="32"
                    class="text-primary mb-1"
                  />
                  <h4 v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data') }}</h4>
                  <h4 v-else class="text-primary">{{ $t('Waiting for filters') }}</h4>
                  <p v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data to generate severe use chart') }}</p>
                  <p v-else class="text-muted">{{ $t('Apply filters to view this chart') }}</p>
                </div>
                <div v-else class="chart-container" style="height: 300px;">
                  <chartjs-component-bar-chart
                    v-if="topSevereUseChartData && topSevereUseChartOptions"
                    :data="topSevereUseChartData"
                    :options="topSevereUseChartOptions"
                    height="300"
                    :key="'severe-use-chart-' + JSON.stringify(chartData.topSevereUseAssets)"
                  />
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Maior Tempo em Uso Ocioso -->
        <b-col lg="6" md="12">
          <b-card no-body>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title>{{ $t('Highest Idle Use') }}</b-card-title>
                <div class="text-muted small">{{ displayDateRange }}</div>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.topIdleUseAssets" rounded="sm" class="d-flex justify-content-center">
                <div v-if="!hasTopIdleUseData" class="no-data-container">
                  <feather-icon
                    v-if="hasLoadedData"
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-muted mb-1"
                  />
                  <feather-icon
                    v-else
                    icon="FilterIcon"
                    size="32"
                    class="text-primary mb-1"
                  />
                  <h4 v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data') }}</h4>
                  <h4 v-else class="text-primary">{{ $t('Waiting for filters') }}</h4>
                  <p v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data to generate idle use chart') }}</p>
                  <p v-else class="text-muted">{{ $t('Apply filters to view this chart') }}</p>
                </div>
                <div v-else class="chart-container" style="height: 300px;">
                  <chartjs-component-bar-chart
                    v-if="topIdleUseChartData && topIdleUseChartOptions"
                    :data="topIdleUseChartData"
                    :options="topIdleUseChartOptions"
                    height="300"
                    :key="'idle-use-chart-' + JSON.stringify(chartData.topIdleUseAssets)"
                  />
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Maior Número de Falhas -->
        <b-col lg="6" md="12">
          <b-card no-body>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-card-title>{{ $t('Highest Number of Failures') }}</b-card-title>
                <div class="text-muted small">{{ displayDateRange }}</div>
              </div>
            </b-card-header>
            <b-card-body>
              <b-overlay :show="isLoading.topFailuresAssets" rounded="sm" class="d-flex justify-content-center">
                <div v-if="!hasTopFailuresData" class="no-data-container">
                  <feather-icon
                    v-if="hasLoadedData"
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-muted mb-1"
                  />
                  <feather-icon
                    v-else
                    icon="FilterIcon"
                    size="32"
                    class="text-primary mb-1"
                  />
                  <h4 v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data') }}</h4>
                  <h4 v-else class="text-primary">{{ $t('Waiting for filters') }}</h4>
                  <p v-if="hasLoadedData" class="text-muted">{{ $t('Not enough data to generate failures chart') }}</p>
                  <p v-else class="text-muted">{{ $t('Apply filters to view this chart') }}</p>
                </div>
                <div v-else class="chart-container" style="height: 350px;">
                  <chartjs-component-bar-chart
                    v-if="topFailuresChartData && topFailuresChartOptions"
                    :data="topFailuresChartData"
                    :options="topFailuresChartOptions"
                    height="350"
                    :key="'failures-chart-' + JSON.stringify(chartData.topFailuresAssets)"
                  />
                </div>
              </b-overlay>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BCol,
  BOverlay,
  BRow,
  BLink,
  BProgress,
  BBadge
} from 'bootstrap-vue'
import store from '@/store'
import { onMounted, watch, computed, ref, onUnmounted } from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import { VueAutosuggest } from 'vue-autosuggest'
import useFleetDashboard from './useFleetDashboard'
import fleetDashboardStoreModule from './fleetDashboardStoreModule'
import customerStoreModule from '@/views/main/customer/customerStoreModule'
import assetStoreModule from '@/views/main/asset/assetStoreModule'
import managementStoreModule from '@/views/main/management/managementStoreModule'
import ChartjsComponentPieChart from '@/views/charts-and-maps/charts/chartjs/charts-components/ChartjsComponentPieChart.vue'
import ChartjsComponentBarChart from '@/views/charts-and-maps/charts/chartjs/charts-components/ChartjsComponentBarChart.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { useToast } from 'vue-toastification/composition'
import vSelect from 'vue-select'
import i18n from '@/libs/i18n'

export default {
  components: {
    BButton,
    BCardBody,
    BCard,
    BRow,
    BCol,
    BCardHeader,
    BCardTitle,
    BOverlay,
    BProgress,
    BBadge,
    flatPickr,
    VueAutosuggest,
    ChartjsComponentPieChart,
    ChartjsComponentBarChart,
    VuePerfectScrollbar,
    BLink,
    ToastificationContent,
    vSelect
  },
  setup() {
    // Registrar os módulos de store necessários
    if (!store.hasModule('fleet-dashboard')) {
      store.registerModule('fleet-dashboard', fleetDashboardStoreModule)
    }
    if (!store.hasModule('app-customer')) {
      store.registerModule('app-customer', customerStoreModule)
    }
    if (!store.hasModule('app-asset')) {
      store.registerModule('app-asset', assetStoreModule)
    }
    if (!store.hasModule('app-management')) {
      store.registerModule('app-management', managementStoreModule)
    }

    // Inicialização do toast e i18n
    const toast = useToast()
    const t = i18n.t.bind(i18n)

    // Painel de filtros
    const isFilterPanelOpen = ref(false)
    
    // Estado para controlar se os dados já foram carregados
    const hasLoadedData = ref(false)

    // Perfect Scrollbar
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    // Usar o hook de composição
    const {
      dateRange,
      searchCustomer,
      customerId,
      assetManufacturerId,
      assetModelId,
      assetTypeId,
      groupId,
      isLoading,
      isLoadingAll,
      chartData,
      assetsCountData,
      updateDashboard,
      loadFleetStatus,
      loadTopConsumptionAssets,
      loadTopSevereUseAssets,
      loadTopIdleUseAssets,
      loadTopFailuresAssets,
      loadAssetsCount
    } = useFleetDashboard()
    
    // Refs para as opções dos filtros
    const manufacturerOptions = ref([])
    const modelOptions = ref([])
    const assetTypeOptions = ref([])

    // Função para carregar fabricantes
    const loadManufacturers = () => {
      store.dispatch('app-asset/fetchAllAssetsManufacture', { page: 1, allData: [] })
        .then(response => {
          manufacturerOptions.value = response.data.data.map(v => ({
            label: v.name,
            value: v.id
          }))
        })
        .catch(() => {})
    }
    
    // Função para carregar modelos baseado no fabricante
    const loadModels = (manufacturerId) => {
      if (!manufacturerId) {
        modelOptions.value = []
        return
      }
      
      store.dispatch('app-asset/fetchAllAssetsModel', { id: manufacturerId, page: 1, allData: [] })
        .then(response => {
          modelOptions.value = response.data.data.map(v => ({
            label: v.name,
            value: v.id
          }))
        })
        .catch(() => {})
    }
    
    // Função para carregar tipos de ativos
    const loadAssetTypes = () => {
      store.dispatch('app-asset/fetchAssetsType')
        .then(response => {
          assetTypeOptions.value = response.data.data.map(v => ({
            label: v.name,
            value: v.id
          }))
        })
        .catch(() => {})
    }
    
    // Watch para recarregar modelos quando o fabricante mudar
    watch(assetManufacturerId, (newVal) => {
      assetModelId.value = ''
      if (newVal) {
        loadModels(newVal)
      } else {
        modelOptions.value = []
      }
    })

    // Quando o componente é montado, inicialize os dados
    onMounted(() => {
      // Definir um valor padrão para o período (últimos 30 dias)
      const today = new Date()
      const oneMonthAgo = new Date(today)
      oneMonthAgo.setMonth(today.getMonth() - 1)
      
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
      }
      
      dateRange.value = `${formatDate(oneMonthAgo)} to ${formatDate(today)}`
      
      // Carregar opções dos filtros
      loadManufacturers()
      loadAssetTypes()
      
      // Carregar apenas os indicadores na inicialização
      loadAssetsCount()
    })

    // Configuração do flatpickr
    const flatpickrConfig = {
      maxDate: new Date(),
      mode: 'range',
      dateFormat: "d/m/Y",
    }

    // Variáveis para o autosuggestion de clientes
    const filteredOptions = ref([])
    const inputProps = {
      id: 'autosuggest__input',
      class: 'form-control auto-suggestions-box',
      placeholder: i18n.t('Customer'),
    }
    
    // Variáveis para o autosuggestion de grupos
    const searchGroup = ref('')
    const filteredOptionsGroup = ref([])
    const inputPropsGroup = computed(() => ({
      id: 'autosuggest__input_group',
      class: 'form-control auto-suggestions-box',
      placeholder: i18n.t('Asset group'),
    }))
    
    // Debounce timer para busca de grupos
    let debounceTimerGroup = null

    // Configuração dos gráficos
    const fleetStatusChartData = computed(() => {
      if (!chartData.fleetStatus) return null

      const statusData = chartData.fleetStatus
      
      return {
        labels: [
          t('Working'),
          t('Not Working'),
          t('No Connection')
        ],
        datasets: [
          {
            data: [
              statusData.highUsage || 0,
              statusData.lowUsage || 0,
              statusData.inactive || 0
            ],
            backgroundColor: ['#28C76F', '#FF9F43', '#f5f4f8'], // Alteração da cor do Inativo para #f5f4f8
            borderWidth: 0,
            label: t('Fleet Status')
          }
        ]
      }
    })

    const fleetStatusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      responsiveAnimationDuration: 500,
      cutoutPercentage: 60, // Criar um espaço no centro para o indicador de falhas
      legend: {
        display: false // Ocultar a legenda
      },
      animation: {
        animateScale: true,
        animateRotate: true
      },
      tooltips: {
        enabled: true,
        callbacks: {
          label(tooltipItem, data) {
            const dataset = data.datasets[tooltipItem.datasetIndex]
            const total = dataset.data.reduce((previousValue, currentValue) => previousValue + currentValue)
            const currentValue = dataset.data[tooltipItem.index]
            const percentage = Math.floor(((currentValue / total) * 100) + 0.5)
            
            return `${data.labels[tooltipItem.index]}: ${currentValue} (${percentage}%)`
          }
        }
      }
    }

    // Função para gerar dados e opções para os gráficos de barras
    const generateBarChartConfig = (data, label, yAxisTitle, color) => {
      if (!data || data.length === 0) return { data: null, options: null }
      
      // Limitar a 10 itens e ordenar
      const chartData = data.slice(0, 10)
      
      // A largura da barra deve ser proporcional ao número máximo de barras (10)
      // independentemente de quantas barras realmente temos
      const categoryPercentage = 0.8 // Espaço disponível para as categorias (80%)
      const barPercentage = 0.6 // Largura da barra em relação ao espaço disponível da categoria
      
      // Calcular o valor máximo para definir o domínio do eixo Y
      const maxValue = chartData.length > 0 
        ? Math.max(...chartData.map(item => item.value || 0))
        : 0
      
      // Arredondar o valor máximo para um número "amigável"
      const ceiling = Math.ceil(maxValue * 1.2 / 10) * 10
      
      return {
        data: {
          labels: chartData.map(item => item.assetId || item.id || 'Desconhecido'),
          datasets: [
            {
              label,
              data: chartData.map(item => item.value || 0),
              backgroundColor: color, // Cor personalizada para cada gráfico
              borderColor: 'transparent',
              borderWidth: 0,
              maxBarThickness: 50, // Largura máxima da barra em pixels
              minBarLength: 5 // Tamanho mínimo da barra para valores muito pequenos
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          responsiveAnimationDuration: 500,
          layout: {
            padding: {
              left: 10,
              right: 10
            }
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                },
                ticks: {
                  autoSkip: true,
                  maxRotation: 90,
                  minRotation: 30
                },
                scaleLabel: {
                  display: false,
                  //labelString: false
                },
                barPercentage: barPercentage,
                categoryPercentage: categoryPercentage
              }
            ],
            yAxes: [
              {
                gridLines: {
                  color: 'rgba(200, 200, 200, 0.2)'
                },
                ticks: {
                  beginAtZero: true,
                  suggestedMin: 0,
                  suggestedMax: ceiling, // Usar o valor máximo calculado
                  maxTicksLimit: 5, // Limitar o número de marcações no eixo Y
                  padding: 10
                },
                scaleLabel: {
                  display: true,
                  labelString: yAxisTitle
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              label(tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex]
                const value = dataset.data[tooltipItem.index]
                const assetId = data.labels[tooltipItem.index]
                
                return `${assetId}: ${value} ${yAxisTitle}`
              }
            }
          }
        }
      }
    }

    // Configurações para cada gráfico de barra com cores personalizadas
    const topConsumptionChartData = computed(() => {
      const config = generateBarChartConfig(chartData.topConsumptionAssets, t('Consumption'), t('Liters'), '#40ac7b')
      return config.data
    })

    const topConsumptionChartOptions = computed(() => {
      const config = generateBarChartConfig(chartData.topConsumptionAssets, t('Consumption'), t('Liters'), '#40ac7b')
      return config.options
    })
    
    const topSevereUseChartData = computed(() => {
      const config = generateBarChartConfig(chartData.topSevereUseAssets, t('Severe Use'), t('Minutes'), '#ffad5f')
      return config.data
    })
    
    const topSevereUseChartOptions = computed(() => {
      const config = generateBarChartConfig(chartData.topSevereUseAssets, t('Severe Use'), t('Minutes'), '#ffad5f')
      return config.options
    })
    
    const topIdleUseChartData = computed(() => {
      const config = generateBarChartConfig(chartData.topIdleUseAssets, t('Iddle Use'), t('Minutes'), '#26d6eb')
      return config.data
    })
    
    const topIdleUseChartOptions = computed(() => {
      const config = generateBarChartConfig(chartData.topIdleUseAssets, t('Iddle Use'), t('Minutes'), '#26d6eb')
      return config.options
    })
    
    const topFailuresChartData = computed(() => {
      const config = generateBarChartConfig(chartData.topFailuresAssets, t('Failures'), t('Number of Failures'), '#ed6d6e')
      return config.data
    })
    
    const topFailuresChartOptions = computed(() => {
      const config = generateBarChartConfig(chartData.topFailuresAssets, t('Failures'), t('Number of Failures'), '#ed6d6e')
      return config.options
    })

    // Funções para manipulação do seletor de clientes
    const onInputChange = (text) => {
      if (text === '' || text === undefined || text.length < 3) {
        customerId.value = ''
        filteredOptions.value = []
        return
      }

      store.dispatch('app-customer/fetchCustomers', {
        q: searchCustomer.value,
        perPage: 5,
        page: 1,
      }).then(response => {
        filteredOptions.value = [{
          data: response.data.data,
        }]
      })
    }

    const selectHandler = (option) => {
      const selected = option.item
      filteredOptions.value = []
      searchCustomer.value = option.item.name
      customerId.value = option.item.id
      
      // Mostrar feedback visual
      toast({
        component: ToastificationContent,
        props: {
          title: t('Customer selected'),
          text: `${option.item.name} - ${t('Click Apply Filters to update')}`,
          icon: 'CheckIcon',
          variant: 'success'
        },
      })
    }

    const clearInput = () => {
      searchCustomer.value = ''
      customerId.value = ''
      filteredOptions.value = []
      
      // Mostrar feedback visual
      toast({
        component: ToastificationContent,
        props: {
          title: t('Customer removed'),
          text: t('Click Apply Filters to update'),
          icon: 'InfoIcon',
          variant: 'info'
        },
      })
    }
    
    // Funções para manipulação do seletor de grupos
    const onInputChangeGroup = (text) => {
      // Limpar o timer anterior
      if (debounceTimerGroup) {
        clearTimeout(debounceTimerGroup)
      }
      
      if (text === '' || text === undefined || text.length < 3) {
        groupId.value = ''
        filteredOptionsGroup.value = []
        return
      }
      
      // Aplicar debounce de 500ms
      debounceTimerGroup = setTimeout(() => {
        store.dispatch('app-management/fetchAllGroup', {
          q: searchGroup.value,
          page: 1,
          perPage: 10
        }).then(response => {
          filteredOptionsGroup.value = [{
            data: response.data.data,
          }]
        }).catch(() => {})
      }, 500)
    }
    
    const selectHandlerGroup = (option) => {
      filteredOptionsGroup.value = []
      searchGroup.value = option.item.name
      groupId.value = option.item.id
      
      // Mostrar feedback visual
      toast({
        component: ToastificationContent,
        props: {
          title: t('Group selected'),
          text: `${option.item.name} - ${t('Click Apply Filters to update')}`,
          icon: 'CheckIcon',
          variant: 'success'
        },
      })
    }
    
    // Função para limpar todos os filtros
    const clearAllFilters = () => {
      searchCustomer.value = ''
      customerId.value = ''
      searchGroup.value = ''
      groupId.value = ''
      assetManufacturerId.value = ''
      assetModelId.value = ''
      assetTypeId.value = ''
      filteredOptions.value = []
      filteredOptionsGroup.value = []
      modelOptions.value = []
      
      // Resetar data para últimos 30 dias
      const today = new Date()
      const oneMonthAgo = new Date(today)
      oneMonthAgo.setMonth(today.getMonth() - 1)
      
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
      }
      
      dateRange.value = `${formatDate(oneMonthAgo)} to ${formatDate(today)}`
      
      // Mostrar feedback visual
      toast({
        component: ToastificationContent,
        props: {
          title: t('Filters cleared'),
          text: t('Click Apply Filters to update'),
          icon: 'InfoIcon',
          variant: 'info'
        },
      })
    }

    const ensureAllowedDateRange = (dates) => {
      if (dateRange.value.includes(" to ")) {
        const [startStr, endStr] = dateRange.value.split(' to ')
        
        if (startStr && endStr) {
          const parseDate = (dateStr) => {
            const [day, month, year] = dateStr.split('/')
            return new Date(`${year}-${month}-${day}`)
          }
          
          const start = parseDate(startStr)
          const end = parseDate(endStr)
          
          // Verificar se o intervalo é maior que um mês
          const minAllowedDate = new Date(end)
          minAllowedDate.setDate(minAllowedDate.getDate() - 31)
          
          if (start < minAllowedDate) {
            // Mostrar notificação usando o toast
            toast({
              component: ToastificationContent,
              props: {
                title: t('Maximum interval of one month'),
                icon: 'InfoIcon',
                variant: 'warning'
              },
            })
            
            // Ajustar a data para o máximo permitido
            const formatDate = (date) => {
              return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
            }
            
            dateRange.value = `${formatDate(minAllowedDate)} to ${endStr}`
          }
        }
      }
    }

    const hasFleetStatusData = computed(() => {
      if (!chartData.fleetStatus) return false
      
      const { highUsage, lowUsage, inactive } = chartData.fleetStatus
      return (highUsage || 0) + (lowUsage || 0) + (inactive || 0) > 0
    })

    const hasTopConsumptionData = computed(() => {
      return chartData.topConsumptionAssets && chartData.topConsumptionAssets.length > 0
    })

    const hasTopSevereUseData = computed(() => {
      return chartData.topSevereUseAssets && chartData.topSevereUseAssets.length > 0
    })

    const hasTopIdleUseData = computed(() => {
      return chartData.topIdleUseAssets && chartData.topIdleUseAssets.length > 0
    })
    
    const hasTopFailuresData = computed(() => {
      return chartData.topFailuresAssets && chartData.topFailuresAssets.length > 0
    })
    
    // Computed property para exibir o período selecionado
    const displayDateRange = computed(() => {
      if (dateRange.value && typeof dateRange.value === 'string' && dateRange.value.includes(' to ')) {
        return dateRange.value.replace(' to ', ' - ')
      }
      return t('Last 30 days')
    })
    
    // Computed property para exibir a data atual
    const currentDate = computed(() => {
      const today = new Date()
      const day = today.getDate().toString().padStart(2, '0')
      const month = (today.getMonth() + 1).toString().padStart(2, '0')
      const year = today.getFullYear()
      return `${day}/${month}/${year}`
    })

    // Desregistrar os módulos quando o componente for desmontado
    onUnmounted(() => {
      if (store.hasModule('fleet-dashboard')) {
        store.unregisterModule('fleet-dashboard')
      }
    })

    const forceUpdateDashboard = () => {
      // Adicionar feedback visual
      toast({
        component: ToastificationContent,
        props: {
          title: t('Updating dashboard'),
          text: t('Reloading all dashboard data'),
          icon: 'RefreshCwIcon',
          variant: 'info'
        },
      })
      
      // Forçar atualização completa dos dados
      chartData.fleetStatus = null
      chartData.topConsumptionAssets = []
      chartData.topSevereUseAssets = []
      chartData.topIdleUseAssets = []
      chartData.topFailuresAssets = []
      assetsCountData.totalAssets = 0
      assetsCountData.byType = []
      assetsCountData.byModel = []
      
      // Marcar que os dados foram carregados
      hasLoadedData.value = true
      
      // Atualizar todos os gráficos
      updateDashboard()
    }

    return {
      dateRange,
      searchCustomer,
      customerId,
      searchGroup,
      groupId,
      assetManufacturerId,
      assetModelId,
      assetTypeId,
      isLoading,
      isLoadingAll,
      chartData,
      assetsCountData,
      updateDashboard,
      flatpickrConfig,
      filteredOptions,
      filteredOptionsGroup,
      inputProps,
      inputPropsGroup,
      onInputChange,
      onInputChangeGroup,
      selectHandler,
      selectHandlerGroup,
      clearInput,
      clearAllFilters,
      ensureAllowedDateRange,
      
      // Opções dos filtros
      manufacturerOptions,
      modelOptions,
      assetTypeOptions,
      
      // Dados e opções dos gráficos
      fleetStatusChartData,
      fleetStatusChartOptions,
      topConsumptionChartData,
      topConsumptionChartOptions,
      topSevereUseChartData,
      topSevereUseChartOptions,
      topIdleUseChartData,
      topIdleUseChartOptions,
      topFailuresChartData,
      topFailuresChartOptions,
      
      // Menu lateral
      isFilterPanelOpen,
      perfectScrollbarSettings,
      hasFleetStatusData,
      hasTopConsumptionData,
      hasTopSevereUseData,
      hasTopIdleUseData,
      hasTopFailuresData,
      displayDateRange,
      currentDate,
      forceUpdateDashboard,
      hasLoadedData
    }
  }
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-autosuggest.scss';
  @import '@core/scss/vue/libs/vue-select.scss';
  @import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  height: 100%;
  width: 100%;
}

/* Estilos do menu lateral (customizer) */
.customizer {
  width: 400px;
  right: -400px;
  padding: 0;
  background-color: #fff;
  z-index: 1031;
  position: fixed;
  top: 0;
  bottom: 0;
  height: 100vh;
  transition: right .4s cubic-bezier(.25,.8,.25,1);
  backface-visibility: hidden;
  border-left: 1px solid rgba(0,0,0,.05);
  box-shadow: 0 0 8px rgba(0,0,0,.1);
  
  &.open {
    right: 0;
  }
  
  .customizer-section {
    padding: 1.5rem;
    border-bottom: 1px solid rgba(0,0,0,.1);
  }
  
  .customizer-toggle {
    position: absolute;
    top: 30%;
    width: 38px;
    height: 38px;
    left: -39px;
    text-align: center;
    background-color: primary;
    color: #fff;
    cursor: pointer;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
    box-shadow: -3px 0 8px rgba(0,0,0,.1);
    background: linear-gradient(118deg,#7367f0,rgba(115,103,240,.7));
  }
  
  .ps-customizer-area {
    height: calc(100% - 83px);
    position: relative;
  }
}

.auto-suggestions-box {
  border-radius: 0.357rem;
}

/* Estilos para o indicador de falhas no centro do gráfico */
.failure-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none; /* Permite cliques passar através do indicador */
}

.failure-circle {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: 50%;
  width: 85px;
  height: 85px;
  transform: translateY(-5%); /* Pequeno ajuste para centralização visual */
}

.alert-falhas {
  background-color: #feecdc;
  border-radius: 0.357rem;
  padding: 0.25rem 0.75rem;
}

.alert-total {
  background-color: #e9f5ff;
  border-radius: 0.357rem;
  padding: 0.25rem 0.75rem;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  text-align: center;
  padding: 2rem;
  
  h4 {
    margin: 1rem 0 0.5rem;
  }
  
  p {
    margin: 0;
    max-width: 300px;
  }
}

.fleet-status-details {
  margin-top: 1rem;
  padding: 0.5rem;
  
  .stats-container {
    max-width: 450px;
    margin: 0 auto;
  }
  
  .status-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
    
    .status-bullet {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      margin-right: 0.5rem;
      
      &.working {
        background-color: #28C76F;
      }
      
      &.not-working {
        background-color: #FF9F43;
      }
      
      &.inactive {
        background-color: #f5f4f8;
        border: 1px solid #ebe9f1;
      }
    }
    
    .status-label {
      font-size: 0.875rem;
    }
    
    .status-value {
      font-weight: bold;
      font-size: 1.25rem;
      margin-top: 0.25rem;
    }
  }
}

.update-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #fff;
  border-radius: 0.357rem;
  padding: 0.5rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  z-index: 1100;
}

.loading-icon {
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.mb-50 {
  margin-bottom: 0.5rem;
}

.mr-25 {
  margin-right: 0.25rem;
}

/* Alert compacto de filtros */
.filter-alert {
  background: linear-gradient(118deg, rgba(115, 103, 240, 0.12), rgba(115, 103, 240, 0.08));
  border-left: 3px solid #7367f0;
  padding: 0.75rem 1.25rem;
  border-radius: 0.357rem;
  box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.14);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(115, 103, 240, 0.22);
    transform: translateY(-1px);
  }
  
  .pulse-icon {
    animation: pulse 2s ease-in-out infinite;
  }
  
  .btn-filter-action {
    padding: 0.4rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Estilos dos cards de estatísticas aprimorados */
.card-statistics-enhanced {
  max-height: 160px; /* Limita altura máxima do card */
  
  .card-body {
    padding: 1rem 1.25rem !important;
    max-height: 160px; /* Limita altura máxima do corpo */
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .statistics-content {
    .avatar {
      margin-bottom: 0;
      width: 42px;
      height: 42px;
      
      .avatar-content {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    
    h2 {
      font-size: 1.85rem !important;
      line-height: 1;
      font-weight: 700 !important;
    }
    
    h6 {
      font-size: 0.9rem;
      line-height: 1.3;
    }
    
    .border-top {
      border-top: 1px solid #ebe9f1 !important;
    }
  }
  
  /* Layout compacto para o card de Total de Ativos */
  &.card-compact {
    .stats-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      
      .stat-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        background-color: #fafafa;
        border-radius: 0.357rem;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: #f0f0f0;
          transform: translateY(-1px);
        }
        
        .stat-icon {
          width: 32px;
          height: 32px;
          border-radius: 0.357rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .stat-content {
          display: flex;
          flex-direction: column;
          min-width: 0; /* Permite truncamento */
          
          .stat-label {
            font-size: 0.7rem;
            color: #6e6b7b;
            font-weight: 500;
            line-height: 1.2;
            margin-bottom: 0.1rem;
          }
          
          .stat-value {
            font-size: 1.15rem;
            font-weight: 700;
            line-height: 1;
          }
        }
      }
    }
  }
}

/* Estilos dos cards de estatísticas originais */
.card-statistics {
  .card-body {
    padding: 1.5rem 1.5rem !important;
  }
  
  .avatar {
    margin-bottom: 0;
  }
}

/* Estilos dos cards de contagem de ativos */
.card-browser-states {
  max-height: 160px; /* Limita altura máxima do card */
  
  .card-header {
    padding: 0.85rem 1.25rem 0.65rem 1.25rem;
    border-bottom: 1px solid #ebe9f1;
  }
  
  .card-body {
    padding: 0.65rem 1.25rem 0.85rem 1.25rem;
    position: relative;
    max-height: 105px; /* Altura máxima do corpo (160 - header ~55px) */
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Garante que o conteúdo não ultrapasse */
  }
}

/* Container scrollável com fade effect */
.scrollable-list-container {
  position: relative;
  max-height: 90px; /* Altura fixa otimizada para ~2 itens visíveis */
  
  .scrollable-content {
    max-height: 90px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 0.5rem;
    
    /* Scrollbar customizada - Webkit (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f8f8f8;
      border-radius: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #d4d4d4;
      border-radius: 10px;
      transition: background 0.3s ease;
      
      &:hover {
        background: #b8b8b8;
      }
    }
    
    /* Scrollbar customizada - Firefox */
    scrollbar-width: thin;
    scrollbar-color: #d4d4d4 #f8f8f8;
  }
  
  /* Fade effect na parte inferior quando há scroll */
  .scroll-fade-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.95));
    pointer-events: none;
    z-index: 1;
  }
}

.asset-count-list {
  .asset-count-item {
    padding: 0.4rem 0;
    transition: background-color 0.2s ease;
    
    &:not(:last-child) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }
    
    &:hover {
      background-color: rgba(115, 103, 240, 0.04);
      border-radius: 0.357rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      margin-left: -0.5rem;
      margin-right: -0.5rem;
    }
    
    .asset-name {
      font-size: 0.8125rem;
      font-weight: 500;
      color: #5e5873;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 150px;
    }
    
    .asset-quantity {
      margin-left: auto;
      padding-left: 0.5rem;
      
      .badge {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
      }
    }
    
    .progress-wrapper {
      margin-top: 0.4rem;
    }
  }
}

.bullet {
  display: inline-block;
  border-radius: 50%;
  
  &.bullet-sm {
    width: 8px;
    height: 8px;
  }
  
  &.bullet-primary {
    background-color: #7367f0;
  }
  
  &.bullet-success {
    background-color: #28c76f;
  }
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .asset-count-list {
    .asset-count-item {
      .asset-name {
        max-width: 200px;
      }
    }
  }
}

@media (max-width: 575.98px) {
  .filter-alert {
    padding: 0.65rem 1rem;
    
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 0.75rem;
    }
    
    .btn-filter-action {
      width: 100%;
    }
  }
  
  .card-statistics {
    .card-body {
      padding: 1.25rem 1rem !important;
    }
    
    h2 {
      font-size: 1.5rem !important;
    }
  }
  
  .card-statistics-enhanced {
    max-height: 150px; /* Altura máxima ajustada para mobile */
    
    .card-body {
      padding: 0.85rem 1rem !important;
      max-height: 150px; /* Altura máxima ajustada para mobile */
    }
    
    .statistics-content {
      .avatar {
        width: 38px;
        height: 38px;
      }
      
      h2 {
        font-size: 1.6rem !important;
      }
      
      h6 {
        font-size: 0.85rem !important;
      }
    }
    
    &.card-compact {
      .stats-grid {
        gap: 0.5rem;
        
        .stat-item {
          padding: 0.4rem;
          
          .stat-icon {
            width: 28px;
            height: 28px;
          }
          
          .stat-content {
            .stat-label {
              font-size: 0.65rem;
            }
            
            .stat-value {
              font-size: 1rem;
            }
          }
        }
      }
    }
  }
  
  .card-browser-states {
    max-height: 150px; /* Altura máxima ajustada para mobile */
    
    .card-header {
      padding: 0.75rem 1rem 0.6rem 1rem;
    }
    
    .card-body {
      padding: 0.6rem 1rem 0.75rem 1rem;
      max-height: 95px; /* Altura máxima ajustada para mobile */
    }
  }
  
  .asset-count-list {
    .asset-count-item {
      .asset-name {
        max-width: 150px;
      }
    }
  }
  
  .scrollable-list-container {
    max-height: 80px; /* Altura fixa otimizada para mobile */
    
    .scrollable-content {
      max-height: 80px;
      padding-right: 0.25rem;
      
      &::-webkit-scrollbar {
        width: 4px; /* Scrollbar mais fina em mobile */
      }
    }
  }
}
</style> 